import React from 'react';
import InfoTooltip from './InfoTooltip';

// Format "YYYY-MM" -> "June 2026"
function formatMonthLabel(monthStr) {
  const [year, month] = monthStr.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

// Calendar heatmap helper: get color based on completion percentage
function getHeatColor(pct) {
  if (pct === null || pct === undefined) return 'bg-gray-100 text-gray-400'; // no data
  if (pct >= 100) return 'bg-green-500 text-white'; // perfect
  if (pct >= 75) return 'bg-green-300 text-green-900'; // high
  if (pct >= 50) return 'bg-yellow-300 text-yellow-900'; // medium
  if (pct >= 25) return 'bg-orange-300 text-orange-900'; // low
  if (pct > 0) return 'bg-red-300 text-red-900'; // minimal
  return 'bg-gray-100 text-gray-400'; // zero
}

// Daily Completion Heatmap for a given month. `scores` is an array of daily
// score rows ({ score_date, total_xp, max_possible_xp, all_targets_met });
// `monthStr` is "YYYY-MM". Display-only — no navigation.
export default function CalendarHeatmap({ scores, monthStr, onPrevMonth, onNextMonth, canGoPrev = false, canGoNext = false }) {
  const [year, month] = monthStr.split('-').map(Number);
  const firstDay = new Date(year, month - 1, 1);
  const daysInMonth = new Date(year, month, 0).getDate();
  const startDayOfWeek = firstDay.getDay(); // 0=Sun

  // Build a map of score_date -> score
  const scoreMap = {};
  (scores || []).forEach(s => {
    scoreMap[s.score_date] = s;
  });

  const todayStr = new Date().toISOString().split('T')[0];

  // Build calendar grid
  const weeks = [];
  let currentWeek = new Array(startDayOfWeek).fill(null);

  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${monthStr}-${String(day).padStart(2, '0')}`;
    const score = scoreMap[dateStr];
    const pct = score && score.max_possible_xp > 0
      ? Math.round((score.total_xp / score.max_possible_xp) * 100)
      : (score ? 0 : null);

    currentWeek.push({ day, dateStr, pct, allMet: score ? !!score.all_targets_met : false, isToday: dateStr === todayStr, hasData: !!score });

    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }

  // Fill remaining days in last week
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) {
      currentWeek.push(null);
    }
    weeks.push(currentWeek);
  }

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthLabel = formatMonthLabel(monthStr);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm mb-6" data-testid="calendar-heatmap">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="text-sm font-medium text-gray-700 truncate">
            Daily Completion Heatmap <span className="text-gray-400 font-normal">· {monthLabel}</span>
          </h3>
          <InfoTooltip text="Each square is a day this month, colored by the % of your daily habits you completed that day: gray (none) → red → green (100%, all targets met). Hover a square for that day's detail." />
        </div>
        {(onPrevMonth || onNextMonth) && (canGoPrev || canGoNext) && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={onPrevMonth}
              disabled={!canGoPrev}
              aria-label="Previous month"
              data-testid="heatmap-prev-month"
              className="p-1 rounded hover:bg-gray-100 text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={onNextMonth}
              disabled={!canGoNext}
              aria-label="Next month"
              data-testid="heatmap-next-month"
              className="p-1 rounded hover:bg-gray-100 text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {dayLabels.map(d => (
          <div key={d} className="text-xs text-gray-400 text-center font-medium">{d}</div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="space-y-1">
        {weeks.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7 gap-1">
            {week.map((cell, ci) => {
              if (!cell) {
                return <div key={ci} className="aspect-square rounded"></div>;
              }

              const colorClass = getHeatColor(cell.pct);

              return (
                <div
                  key={ci}
                  className={`aspect-square rounded flex items-center justify-center text-xs font-medium ${colorClass} ${cell.isToday ? 'ring-2 ring-primary ring-offset-1' : ''}`}
                  title={cell.hasData ? `${cell.dateStr}: ${cell.pct}% complete${cell.allMet ? ' (all targets met!)' : ''}` : `${cell.dateStr}: No activity`}
                  data-testid={`heatmap-day-${cell.day}`}
                >
                  {cell.day}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-2 mt-3 text-xs text-gray-500">
        <span>Less</span>
        <div className="w-4 h-4 rounded bg-gray-100"></div>
        <div className="w-4 h-4 rounded bg-red-300"></div>
        <div className="w-4 h-4 rounded bg-orange-300"></div>
        <div className="w-4 h-4 rounded bg-yellow-300"></div>
        <div className="w-4 h-4 rounded bg-green-300"></div>
        <div className="w-4 h-4 rounded bg-green-500"></div>
        <span>More</span>
      </div>
    </div>
  );
}
